import {
    integrationApi,
    IntegrationCategory,
    IntegrationModule,
} from '@/api/integrations';
import api from '@/lib/axios';
import { useAuthStore } from '@/store/authStore';
import { Country, Currency, Invoice, SubscriptionPlan } from '@/types';
import {
    BellIcon,
    BuildingStorefrontIcon,
    CreditCardIcon,
    GlobeAltIcon,
    ShieldCheckIcon,
    UserCircleIcon,
} from '@heroicons/react/24/outline';
import { FC, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

const SettingsContainer = styled.div`
  min-height: 100vh;
  background: var(--gray-50);
`;

const SettingsContent = styled.div`
  padding: 2.5rem 0;
`;

const Header = styled.header`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const Main = styled.main`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1rem;
  box-shadow: var(--shadow-sm);
  height: fit-content;
`;

const NavItem = styled.button<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: none;
  background: ${(props) =>
    props.$active ? 'var(--primary-50)' : 'transparent'};
  color: ${(props) =>
    props.$active ? 'var(--primary-600)' : 'var(--gray-700)'};
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    background: ${(props) =>
      props.$active ? 'var(--primary-50)' : 'var(--gray-50)'};
    color: ${(props) =>
      props.$active ? 'var(--primary-600)' : 'var(--gray-900)'};
  }
`;

const ContentPanel = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 2rem;
  box-shadow: var(--shadow-sm);
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
`;

const SectionDescription = styled.p`
  font-size: 0.875rem;
  color: var(--gray-600);
  margin-bottom: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FormGroup = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  grid-column: ${(props) => (props.$fullWidth ? '1 / -1' : 'auto')};
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-700);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--gray-900);
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }

  &:disabled {
    background: var(--gray-100);
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--gray-900);
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--gray-900);
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const InfoText = styled.p`
  font-size: 0.75rem;
  color: var(--gray-500);
  margin-top: 0.25rem;
`;

const ToggleGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: var(--gray-50);
  border-radius: 0.5rem;
  margin-bottom: 1rem;
`;

const ToggleLabel = styled.div`
  flex: 1;

  h4 {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--gray-900);
    margin-bottom: 0.25rem;
  }

  p {
    font-size: 0.75rem;
    color: var(--gray-600);
  }
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--gray-300);
    transition: 0.3s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: '';
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }

  input:checked + span {
    background: var(--primary-600);
  }

  input:checked + span:before {
    transform: translateX(20px);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--gray-200);
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${(props) =>
    props.$variant === 'secondary'
      ? `
    background: white;
    color: var(--gray-700);
    border: 1px solid var(--gray-300);
    
    &:hover {
      background: var(--gray-50);
    }
  `
      : `
    background: var(--primary-600);
    color: white;
    
    &:hover {
      background: var(--primary-700);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
    }
  `}
`;

const IntegrationSection = styled.div`
  margin-bottom: 2.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const IntegrationSectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 1rem;
`;

const IntegrationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
`;

const IntegrationCard = styled.div`
  border: 1px solid var(--gray-200);
  border-radius: 0.75rem;
  padding: 1.5rem;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: var(--gray-300);
  }
`;

const IntegrationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const IntegrationLogo = styled.div<{ $color: string }>`
  width: 3rem;
  height: 3rem;
  border-radius: 0.5rem;
  background: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 1.75rem;
    height: 1.75rem;
    color: white;
  }
`;

const IntegrationInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const IntegrationName = styled.h4`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 0.25rem;
`;

const IntegrationStatus = styled.span<{ $connected: boolean }>`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${(props) =>
    props.$connected ? 'var(--success-700)' : 'var(--gray-600)'};
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(props) =>
      props.$connected ? 'var(--success-600)' : 'var(--gray-400)'};
  }
`;

const IntegrationDescription = styled.p`
  font-size: 0.8125rem;
  color: var(--gray-600);
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const IntegrationActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IntegrationButton = styled.button<{
  $variant?: 'primary' | 'secondary' | 'danger';
}>`
  flex: 1;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${(props) => {
    if (props.$variant === 'danger') {
      return `
        background: white;
        color: var(--error-700);
        border-color: var(--error-300);
        
        &:hover {
          background: var(--error-50);
          border-color: var(--error-400);
        }
      `;
    } else if (props.$variant === 'secondary') {
      return `
        background: white;
        color: var(--gray-700);
        border-color: var(--gray-300);
        
        &:hover {
          background: var(--gray-50);
          border-color: var(--gray-400);
        }
      `;
    } else {
      return `
        background: var(--primary-600);
        color: white;
        border-color: var(--primary-600);
        
        &:hover {
          background: var(--primary-700);
          border-color: var(--primary-700);
        }
      `;
    }
  }}
`;

/** Renders a text abbreviation inside an integration logo box. */
const IntegrationIconGlyph: FC<{ slug: string }> = ({ slug }) => {
  const GLYPHS: Record<string, string> = {
    amazon: 'Amz',
    ebay: 'eBay',
    etsy: 'Etsy',
    shopify: 'Sfy',
    magento2: 'Mg2',
    woocommerce: 'Woo',
    temu: 'Temu',
    shein: 'Shin',
    aliexpress: 'AliX',
    alibaba: 'Ali',
    allegro: 'Alg',
    bol: 'Bol',
    kaufland: 'Kauf',
    instagram: 'Insta',
    stripe: 'Str',
    revolut: 'Rev',
    paypal: 'PP',
    dhl: 'DHL',
    fedex: 'FdX',
    ups: 'UPS',
  };
  return (
    <span
      style={{
        fontWeight: 700,
        fontSize: '0.7rem',
        letterSpacing: '-0.02em',
        color: 'white',
      }}
    >
      {GLYPHS[slug] ?? slug.slice(0, 3).toUpperCase()}
    </span>
  );
};

// ── Configure modal styled components ─────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalPanel = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 540px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--gray-200);
`;

const ModalTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--gray-400);
  padding: 0.25rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  transition: color 0.15s;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover {
    color: var(--gray-700);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.125rem;
  overflow-y: auto;
  max-height: 70vh;
`;

const ModalFormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const ModalLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--gray-700);
`;

const ModalInput = styled.input`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const ModalSelect = styled.select`
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  width: 100%;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const ModalToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalToggleLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--gray-700);
`;

const ModalToggleDesc = styled.div`
  font-size: 0.75rem;
  color: var(--gray-400);
  margin-top: 0.125rem;
`;

const ModalToggle = styled.input`
  width: 2.75rem;
  height: 1.5rem;
  cursor: pointer;
  accent-color: var(--primary-600);
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.125rem 1.5rem;
  border-top: 1px solid var(--gray-200);
`;

const ModalCancelBtn = styled.button`
  padding: 0.625rem 1.25rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  background: white;
  color: var(--gray-700);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background: var(--gray-50);
  }
`;

const ModalSaveBtn = styled.button`
  padding: 0.625rem 1.25rem;
  border: 1px solid var(--primary-600);
  border-radius: 0.5rem;
  background: var(--primary-600);
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background: var(--primary-700);
  }
`;

// ── Billing styled components ──────────────────────────────────────────────
const BillingSection = styled.div`
  margin-bottom: 2rem;
`;

const BillingSectionTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--gray-800);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--gray-100);
`;

const CurrentPlanCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, var(--primary-50) 0%, white 100%);
  border: 1px solid var(--primary-200);
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 1rem;
`;

const PlanCard = styled.div<{ $featured?: boolean; $current?: boolean }>`
  border: 2px solid
    ${(p) =>
      p.$current
        ? 'var(--primary-500)'
        : p.$featured
          ? 'var(--primary-200)'
          : 'var(--gray-200)'};
  border-radius: 0.75rem;
  padding: 1.25rem;
  position: relative;
  background: ${(p) => (p.$current ? 'var(--primary-50)' : 'white')};
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const PlanBadge = styled.span<{ $primary?: boolean }>`
  position: absolute;
  top: -10px;
  right: 12px;
  background: ${(p) => (p.$primary ? 'var(--primary-600)' : 'var(--gray-500)')};
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const BillingPlanName = styled.h4`
  font-size: 1rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 0.2rem;
`;

const BillingPlanDesc = styled.p`
  font-size: 0.8rem;
  color: var(--gray-500);
  margin-bottom: 0.75rem;
`;

const BillingPlanPrice = styled.div`
  font-size: 1.625rem;
  font-weight: 700;
  color: var(--primary-600);
  margin-bottom: 0.875rem;
  span {
    font-size: 0.875rem;
    font-weight: 400;
    color: var(--gray-500);
  }
`;

const BillingFeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.25rem;
  font-size: 0.8125rem;
  color: var(--gray-600);
  flex: 1;
  li {
    padding: 0.2rem 0;
  }
  li::before {
    content: '✓ ';
    color: var(--primary-500);
    font-weight: 700;
  }
`;

const PlanChooseBtn = styled.button<{ $current?: boolean }>`
  width: 100%;
  padding: 0.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${(p) => (p.$current ? 'default' : 'pointer')};
  border: 2px solid
    ${(p) => (p.$current ? 'var(--gray-300)' : 'var(--primary-600)')};
  background: ${(p) => (p.$current ? 'var(--gray-100)' : 'var(--primary-600)')};
  color: ${(p) => (p.$current ? 'var(--gray-500)' : 'white')};
  transition: all 0.2s;
  &:hover:not(:disabled) {
    background: ${(p) =>
      p.$current ? 'var(--gray-100)' : 'var(--primary-700)'};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  thead tr {
    background: var(--gray-50);
  }
  th,
  td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid var(--gray-100);
  }
  th {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--gray-500);
  }
  tbody tr:hover {
    background: var(--gray-50);
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(p) =>
    ({
      paid: '#dcfce7',
      pending: '#fef9c3',
      overdue: '#fee2e2',
      cancelled: '#f3f4f6',
    })[p.$status] ?? '#f3f4f6'};
  color: ${(p) =>
    ({
      paid: '#166534',
      pending: '#854d0e',
      overdue: '#991b1b',
      cancelled: '#6b7280',
    })[p.$status] ?? '#6b7280'};
`;

const InvPayBtn = styled.button`
  padding: 0.275rem 0.65rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  margin-right: 0.4rem;
  border: none;
  background: var(--primary-600);
  color: white;
  transition: background 0.15s;
  &:hover:not(:disabled) {
    background: var(--primary-700);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InvPrintBtn = styled.button`
  padding: 0.275rem 0.65rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  background: white;
  color: var(--gray-700);
  border: 1px solid var(--gray-300);
  transition: background 0.15s;
  &:hover {
    background: var(--gray-50);
  }
`;

const Settings: FC = () => {
  const { user, setAuth, tokens } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [bio, setBio] = useState(user?.bio ?? '');
  const [companyName, setCompanyName] = useState(user?.tenant_name ?? '');
  const [countryId, setCountryId] = useState(user?.country_id ?? '');
  const [industry, setIndustry] = useState(user?.industry ?? '');
  const [companySize, setCompanySize] = useState(user?.company_size ?? '');
  const [address, setAddress] = useState(user?.address ?? '');
  const [city, setCity] = useState(user?.city ?? '');
  const [postalCode, setPostalCode] = useState(user?.postal_code ?? '');
  const [countries, setCountries] = useState<Country[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [timezone, setTimezone] = useState(user?.timezone ?? 'UTC');
  const [currencyCode, setCurrencyCode] = useState(user?.currency_code ?? 'USD');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [notifications, setNotifications] = useState({
    email: user?.notification_preferences?.email ?? true,
    orderUpdates: user?.notification_preferences?.order_updates ?? true,
    marketing: user?.notification_preferences?.marketing ?? false,
    security: user?.notification_preferences?.security ?? true,
  });
  const [integrationGroups, setIntegrationGroups] = useState<
    IntegrationCategory[]
  >([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationsError, setIntegrationsError] = useState<string | null>(
    null
  );
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [subscribing, setSubscribing] = useState<number | null>(null);
  const [payingInvoice, setPayingInvoice] = useState<string | null>(null);
  const [configuringIntegration, setConfiguringIntegration] =
    useState<IntegrationModule | null>(null);
  const [configForm, setConfigForm] = useState({
    name: '',
    apiKey: '',
    apiSecret: '',
    region: '',
    autoSync: false,
    syncInterval: '30',
  });

  const openConfigure = (mod: IntegrationModule) => {
    setConfiguringIntegration(mod);
    setConfigForm({
      name: mod.name,
      apiKey: '????????????????????????????????',
      apiSecret: '????????????????????????????????',
      region: mod.slug === 'amazon' ? 'us-east-1' : '',
      autoSync: true,
      syncInterval: '30',
    });
  };

  const closeConfigure = () => setConfiguringIntegration(null);

  useEffect(() => {
    api
      .get('/tenants/countries')
      .then((res) => setCountries(res.data.data ?? []))
      .catch(() => {
        /* non-critical */
      });
  }, []);

  useEffect(() => {
    api
      .get('/tenants/currencies')
      .then((res) => setCurrencies(res.data.data ?? []))
      .catch(() => {
        /* non-critical */
      });
  }, []);

  useEffect(() => {
    if (activeTab === 'integrations' && integrationGroups.length === 0) {
      setIntegrationsLoading(true);
      setIntegrationsError(null);
      integrationApi
        .listAll()
        .then(setIntegrationGroups)
        .catch(() => setIntegrationsError('Failed to load integrations'))
        .finally(() => setIntegrationsLoading(false));
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'billing') {
      setBillingLoading(true);
      Promise.all([api.get('/billing/plans'), api.get('/billing/invoices')])
        .then(([plansRes, invoicesRes]) => {
          setPlans(plansRes.data.data ?? []);
          setInvoices(invoicesRes.data.data ?? []);
        })
        .catch(() => toast.error('Failed to load billing information'))
        .finally(() => setBillingLoading(false));
    }
  }, [activeTab]);

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const [firstName, ...rest] = fullName.trim().split(' ');
      const lastName = rest.join(' ') || firstName;
      await api.patch('/users/me', {
        first_name: firstName,
        last_name: lastName,
        phone: phone.trim() || null,
        bio: bio.trim() || null,
      });
      if (user && tokens) {
        setAuth(
          {
            ...user,
            full_name: fullName,
            phone: phone.trim() || undefined,
            bio: bio.trim() || undefined,
          },
          tokens
        );
      }
      toast.success('Profile saved successfully');
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSaveCompany = async () => {
    setIsSavingCompany(true);
    try {
      await api.patch('/tenants/me', {
        company_name: companyName,
        ...(countryId ? { country_id: countryId } : {}),
        industry: industry || null,
        company_size: companySize || null,
        address: address || null,
        city: city || null,
        postal_code: postalCode || null,
        timezone: timezone || 'UTC',
        currency_code: currencyCode || 'USD',
      });
      if (user && tokens) {
        setAuth(
          {
            ...user,
            tenant_name: companyName,
            country_id: countryId || undefined,
            industry: industry || undefined,
            company_size: companySize || undefined,
            address: address || undefined,
            city: city || undefined,
            postal_code: postalCode || undefined,
            timezone: timezone || undefined,
            currency_code: currencyCode || undefined,
          },
          tokens
        );
      }
      toast.success('Company settings saved successfully');
    } catch {
      toast.error('Failed to save company settings');
    } finally {
      setIsSavingCompany(false);
    }
  };

  const handleSaveNotifications = async () => {
    setIsSavingNotifications(true);
    try {
      await api.patch('/users/me', {
        notification_preferences: {
          email: notifications.email,
          order_updates: notifications.orderUpdates,
          marketing: notifications.marketing,
          security: notifications.security,
        },
      });
      if (user && tokens) {
        setAuth(
          {
            ...user,
            notification_preferences: {
              email: notifications.email,
              order_updates: notifications.orderUpdates,
              marketing: notifications.marketing,
              security: notifications.security,
            },
          },
          tokens
        );
      }
      toast.success('Notification preferences saved');
    } catch {
      toast.error('Failed to save notification preferences');
    } finally {
      setIsSavingNotifications(false);
    }
  };

  const handleSubscribe = async (planId: number) => {
    setSubscribing(planId);
    try {
      const res = await api.post('/billing/subscribe', { plan_id: planId });
      const newInvoice: Invoice = res.data.data.invoice;
      const chosenPlan = plans.find((p) => p.id === planId);
      setInvoices((prev) => [newInvoice, ...prev]);
      if (user && tokens && chosenPlan) {
        setAuth(
          {
            ...user,
            subscription_tier: chosenPlan.slug,
            subscription_started_at: new Date().toISOString(),
          },
          tokens
        );
      }
      toast.success('Subscription updated successfully');
    } catch {
      toast.error('Failed to update subscription');
    } finally {
      setSubscribing(null);
    }
  };

  const handlePayInvoice = async (invoiceId: string) => {
    setPayingInvoice(invoiceId);
    try {
      const res = await api.post(`/billing/invoices/${invoiceId}/pay`);
      setInvoices((prev) =>
        prev.map((inv) => (inv.id === invoiceId ? res.data.data : inv))
      );
      toast.success('Payment recorded successfully');
    } catch {
      toast.error('Failed to process payment');
    } finally {
      setPayingInvoice(null);
    }
  };

  const printInvoice = (invoice: Invoice) => {
    const html = `<!DOCTYPE html><html><head><title>Invoice ${invoice.invoice_number}</title>
<style>
  body{font-family:Arial,sans-serif;max-width:800px;margin:40px auto;color:#111}
  .hdr{display:flex;justify-content:space-between;border-bottom:2px solid #ea580c;padding-bottom:16px;margin-bottom:24px}
  .co{font-size:22px;font-weight:700;color:#ea580c}.inv-title{font-size:28px;font-weight:700;color:#ea580c;text-align:right}
  .inv-num{color:#666;font-size:13px}.meta{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:28px}
  .mg label{font-size:11px;text-transform:uppercase;color:#888;font-weight:600}.mg p{font-weight:600;margin:4px 0 0}
  table{width:100%;border-collapse:collapse;margin-bottom:20px}
  th{background:#f5f5f5;padding:9px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#666}
  td{padding:11px 14px;border-bottom:1px solid #eee}
  .tot{font-weight:700;background:#fafafa}
  .badge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:12px;font-weight:600;
    background:${invoice.status === 'paid' ? '#dcfce7' : '#fef9c3'};
    color:${invoice.status === 'paid' ? '#166534' : '#854d0e'}}
  .footer{text-align:center;font-size:12px;color:#999;margin-top:36px;border-top:1px solid #eee;padding-top:14px}
</style></head><body>
<div class="hdr">
  <div><div class="co">Marketeer Commerce</div><div style="color:#666;font-size:13px;margin-top:4px">marketeer.commerce</div></div>
  <div><div class="inv-title">INVOICE</div><div class="inv-num">#${invoice.invoice_number}</div></div>
</div>
<div class="meta">
  <div class="mg"><label>Date</label><p>${new Date(invoice.created_at).toLocaleDateString()}</p></div>
  <div class="mg"><label>Status</label><p><span class="badge">${invoice.status.toUpperCase()}</span></p></div>
  <div class="mg"><label>Due Date</label><p>${invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '—'}</p></div>
  ${invoice.paid_at ? `<div class="mg"><label>Paid On</label><p>${new Date(invoice.paid_at).toLocaleDateString()}</p></div>` : ''}
</div>
<table>
  <thead><tr><th>Description</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit Price</th><th style="text-align:right">Total</th></tr></thead>
  <tbody>
    ${(invoice.line_items || []).map((item) => `<tr><td>${item.description}</td><td style="text-align:right">${item.quantity}</td><td style="text-align:right">$${item.unit_price.toFixed(2)}</td><td style="text-align:right">$${item.total.toFixed(2)}</td></tr>`).join('')}
    <tr class="tot"><td colspan="3" style="text-align:right">Total</td><td style="text-align:right">$${invoice.amount.toFixed(2)} ${invoice.currency}</td></tr>
  </tbody>
</table>
<div class="footer">Thank you for your business. Questions? billing@marketeer.commerce</div>
</body></html>`;
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.print();
    }
  };

  const handleConnect = async (slug: string) => {
    try {
      await integrationApi.connect(slug);
      setIntegrationGroups((prev) =>
        prev.map((group) => ({
          ...group,
          integrations: group.integrations.map((mod) =>
            mod.slug === slug ? { ...mod, is_connected: true } : mod
          ),
        }))
      );
    } catch {
      // silently leave state unchanged; could add toast
    }
  };

  const handleDisconnect = async (slug: string) => {
    try {
      await integrationApi.disconnect(slug);
      setIntegrationGroups((prev) =>
        prev.map((group) => ({
          ...group,
          integrations: group.integrations.map((mod) =>
            mod.slug === slug ? { ...mod, is_connected: false } : mod
          ),
        }))
      );
    } catch {
      // silently leave state unchanged
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: UserCircleIcon },
    { id: 'company', label: 'Company', icon: BuildingStorefrontIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'security', label: 'Security', icon: ShieldCheckIcon },
    { id: 'billing', label: 'Billing', icon: CreditCardIcon },
    { id: 'integrations', label: 'Integrations', icon: GlobeAltIcon },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <>
            <SectionTitle>Profile Settings</SectionTitle>
            <SectionDescription>
              Update your personal information and preferences
            </SectionDescription>
            <FormGrid>
              <FormGroup>
                <Label>Full Name</Label>
                <Input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>Email Address</Label>
                <Input type="email" defaultValue={user?.email} disabled />
                <InfoText>Email cannot be changed</InfoText>
              </FormGroup>
              <FormGroup>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>Role</Label>
                <Input
                  type="text"
                  defaultValue={user?.roles?.[0] ?? user?.role ?? ''}
                  disabled
                />
              </FormGroup>
              <FormGroup $fullWidth>
                <Label>Bio</Label>
                <TextArea
                  placeholder="Tell us about yourself..."
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                />
              </FormGroup>
            </FormGrid>
            <ButtonGroup>
              <Button onClick={handleSaveProfile} disabled={isSavingProfile}>
                {isSavingProfile ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                $variant="secondary"
                onClick={() => {
                  setFullName(user?.full_name ?? '');
                  setPhone(user?.phone ?? '');
                  setBio(user?.bio ?? '');
                }}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </>
        );

      case 'company':
        return (
          <>
            <SectionTitle>Company Settings</SectionTitle>
            <SectionDescription>
              Manage your company information and branding
            </SectionDescription>
            <FormGrid>
              <FormGroup $fullWidth>
                <Label>Company Name</Label>
                <Input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>Industry</Label>
                <Select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                >
                  <option value="">— Select industry —</option>
                  <option value="E-commerce">E-commerce</option>
                  <option value="Retail">Retail</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Services">Services</option>
                  <option value="Technology">Technology</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Finance">Finance</option>
                  <option value="Other">Other</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Company Size</Label>
                <Select
                  value={companySize}
                  onChange={(e) => setCompanySize(e.target.value)}
                >
                  <option value="">— Select size —</option>
                  <option value="1-10 employees">1-10 employees</option>
                  <option value="11-50 employees">11-50 employees</option>
                  <option value="51-200 employees">51-200 employees</option>
                  <option value="200+ employees">200+ employees</option>
                </Select>
              </FormGroup>
              <FormGroup $fullWidth>
                <Label>Company Address</Label>
                <Input
                  type="text"
                  placeholder="123 Main St"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>City</Label>
                <Input
                  type="text"
                  placeholder="New York"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>Postal Code</Label>
                <Input
                  type="text"
                  placeholder="10001"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>Country</Label>
                <Select
                  value={countryId}
                  onChange={(e) => setCountryId(e.target.value)}
                >
                  <option value="">— Select country —</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Timezone</Label>
                <Select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <option value="UTC">UTC</option>
                  <option value="Europe/London">Europe/London (GMT+0)</option>
                  <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
                  <option value="Europe/Berlin">Europe/Berlin (GMT+1)</option>
                  <option value="Europe/Ljubljana">Europe/Ljubljana (GMT+1)</option>
                  <option value="Europe/Vienna">Europe/Vienna (GMT+1)</option>
                  <option value="Europe/Zurich">Europe/Zurich (GMT+1)</option>
                  <option value="Europe/Rome">Europe/Rome (GMT+1)</option>
                  <option value="Europe/Madrid">Europe/Madrid (GMT+1)</option>
                  <option value="Europe/Amsterdam">Europe/Amsterdam (GMT+1)</option>
                  <option value="Europe/Brussels">Europe/Brussels (GMT+1)</option>
                  <option value="Europe/Warsaw">Europe/Warsaw (GMT+1)</option>
                  <option value="Europe/Prague">Europe/Prague (GMT+1)</option>
                  <option value="Europe/Budapest">Europe/Budapest (GMT+1)</option>
                  <option value="Europe/Bucharest">Europe/Bucharest (GMT+2)</option>
                  <option value="Europe/Sofia">Europe/Sofia (GMT+2)</option>
                  <option value="Europe/Athens">Europe/Athens (GMT+2)</option>
                  <option value="Europe/Helsinki">Europe/Helsinki (GMT+2)</option>
                  <option value="Europe/Riga">Europe/Riga (GMT+2)</option>
                  <option value="Europe/Vilnius">Europe/Vilnius (GMT+2)</option>
                  <option value="Europe/Tallinn">Europe/Tallinn (GMT+2)</option>
                  <option value="Europe/Kiev">Europe/Kiev (GMT+2)</option>
                  <option value="Europe/Moscow">Europe/Moscow (GMT+3)</option>
                  <option value="Asia/Dubai">Asia/Dubai (GMT+4)</option>
                  <option value="Asia/Kolkata">Asia/Kolkata (GMT+5:30)</option>
                  <option value="Asia/Bangkok">Asia/Bangkok (GMT+7)</option>
                  <option value="Asia/Singapore">Asia/Singapore (GMT+8)</option>
                  <option value="Asia/Shanghai">Asia/Shanghai (GMT+8)</option>
                  <option value="Asia/Tokyo">Asia/Tokyo (GMT+9)</option>
                  <option value="Australia/Sydney">Australia/Sydney (GMT+10)</option>
                  <option value="Pacific/Auckland">Pacific/Auckland (GMT+12)</option>
                  <option value="America/New_York">America/New_York (GMT-5)</option>
                  <option value="America/Chicago">America/Chicago (GMT-6)</option>
                  <option value="America/Denver">America/Denver (GMT-7)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (GMT-8)</option>
                  <option value="America/Anchorage">America/Anchorage (GMT-9)</option>
                  <option value="Pacific/Honolulu">Pacific/Honolulu (GMT-10)</option>
                  <option value="America/Sao_Paulo">America/Sao_Paulo (GMT-3)</option>
                  <option value="America/Buenos_Aires">America/Buenos_Aires (GMT-3)</option>
                  <option value="America/Mexico_City">America/Mexico_City (GMT-6)</option>
                  <option value="America/Toronto">America/Toronto (GMT-5)</option>
                  <option value="America/Vancouver">America/Vancouver (GMT-8)</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label>Currency</Label>
                <Select
                  value={currencyCode}
                  onChange={(e) => setCurrencyCode(e.target.value)}
                >
                  {currencies.length === 0 && (
                    <option value="USD">USD — United States Dollar ($)</option>
                  )}
                  {currencies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.name} ({c.symbol})
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>
            <ButtonGroup>
              <Button onClick={handleSaveCompany} disabled={isSavingCompany}>
                {isSavingCompany ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                $variant="secondary"
                onClick={() => {
                  setCompanyName(user?.tenant_name ?? '');
                  setCountryId(user?.country_id ?? '');
                  setIndustry(user?.industry ?? '');
                  setCompanySize(user?.company_size ?? '');
                  setAddress(user?.address ?? '');
                  setCity(user?.city ?? '');
                  setPostalCode(user?.postal_code ?? '');
                  setTimezone(user?.timezone ?? 'UTC');
                  setCurrencyCode(user?.currency_code ?? 'USD');
                }}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </>
        );

      case 'notifications':
        return (
          <>
            <SectionTitle>Notification Preferences</SectionTitle>
            <SectionDescription>
              Choose how you want to be notified about updates
            </SectionDescription>
            <div>
              <ToggleGroup>
                <ToggleLabel>
                  <h4>Email Notifications</h4>
                  <p>Receive notifications via email</p>
                </ToggleLabel>
                <Toggle>
                  <input
                    type="checkbox"
                    checked={notifications.email}
                    onChange={(e) =>
                      setNotifications({
                        ...notifications,
                        email: e.target.checked,
                      })
                    }
                  />
                  <span></span>
                </Toggle>
              </ToggleGroup>

              <ToggleGroup>
                <ToggleLabel>
                  <h4>Order Updates</h4>
                  <p>Get notified about order status changes</p>
                </ToggleLabel>
                <Toggle>
                  <input
                    type="checkbox"
                    checked={notifications.orderUpdates}
                    onChange={(e) =>
                      setNotifications({
                        ...notifications,
                        orderUpdates: e.target.checked,
                      })
                    }
                  />
                  <span></span>
                </Toggle>
              </ToggleGroup>

              <ToggleGroup>
                <ToggleLabel>
                  <h4>Marketing Communications</h4>
                  <p>Receive news, updates, and offers</p>
                </ToggleLabel>
                <Toggle>
                  <input
                    type="checkbox"
                    checked={notifications.marketing}
                    onChange={(e) =>
                      setNotifications({
                        ...notifications,
                        marketing: e.target.checked,
                      })
                    }
                  />
                  <span></span>
                </Toggle>
              </ToggleGroup>

              <ToggleGroup>
                <ToggleLabel>
                  <h4>Security Alerts</h4>
                  <p>Important security and account notifications</p>
                </ToggleLabel>
                <Toggle>
                  <input
                    type="checkbox"
                    checked={notifications.security}
                    onChange={(e) =>
                      setNotifications({
                        ...notifications,
                        security: e.target.checked,
                      })
                    }
                  />
                  <span></span>
                </Toggle>
              </ToggleGroup>
            </div>
            <ButtonGroup>
              <Button
                onClick={handleSaveNotifications}
                disabled={isSavingNotifications}
              >
                {isSavingNotifications ? 'Saving...' : 'Save Preferences'}
              </Button>
            </ButtonGroup>
          </>
        );

      case 'security':
        return (
          <>
            <SectionTitle>Security Settings</SectionTitle>
            <SectionDescription>
              Manage your password and security preferences
            </SectionDescription>
            <FormGrid>
              <FormGroup $fullWidth>
                <Label>Current Password</Label>
                <Input type="password" placeholder="••••••••" />
              </FormGroup>
              <FormGroup>
                <Label>New Password</Label>
                <Input type="password" placeholder="••••••••" />
              </FormGroup>
              <FormGroup>
                <Label>Confirm New Password</Label>
                <Input type="password" placeholder="••••••••" />
              </FormGroup>
            </FormGrid>
            <ButtonGroup>
              <Button>Update Password</Button>
              <Button $variant="secondary">Cancel</Button>
            </ButtonGroup>
          </>
        );

      case 'billing': {
        const currentPlan = plans.find(
          (p) => p.slug === user?.subscription_tier
        );
        return (
          <>
            <SectionTitle>Billing &amp; Subscription</SectionTitle>
            <SectionDescription>
              Manage your subscription plan and view billing history
            </SectionDescription>

            {billingLoading ? (
              <div
                style={{
                  padding: '3rem',
                  textAlign: 'center',
                  color: 'var(--gray-500)',
                }}
              >
                Loading billing information…
              </div>
            ) : (
              <>
                {/* Current plan */}
                {currentPlan && (
                  <BillingSection>
                    <BillingSectionTitle>Current Plan</BillingSectionTitle>
                    <CurrentPlanCard>
                      <div>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            color: 'var(--gray-900)',
                          }}
                        >
                          {currentPlan.name}
                        </div>
                        <div
                          style={{
                            color: 'var(--gray-500)',
                            fontSize: '0.875rem',
                            marginTop: '0.2rem',
                          }}
                        >
                          {currentPlan.price === 0
                            ? 'Free forever'
                            : `$${currentPlan.price}/${currentPlan.billing_period}`}
                          {user?.subscription_started_at &&
                            ` · Active since ${new Date(user.subscription_started_at).toLocaleDateString()}`}
                        </div>
                      </div>
                      <span
                        style={{
                          padding: '0.3rem 1rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          background: 'var(--primary-100)',
                          color: 'var(--primary-700)',
                        }}
                      >
                        {user?.subscription_status ?? 'Active'}
                      </span>
                    </CurrentPlanCard>
                  </BillingSection>
                )}

                {/* Plan selection */}
                <BillingSection>
                  <BillingSectionTitle>Choose a Plan</BillingSectionTitle>
                  <PlansGrid>
                    {plans.map((plan) => {
                      const isCurrent = plan.slug === user?.subscription_tier;
                      return (
                        <PlanCard
                          key={plan.id}
                          $featured={plan.is_featured}
                          $current={isCurrent}
                        >
                          {plan.is_featured && !isCurrent && (
                            <PlanBadge $primary>Most Popular</PlanBadge>
                          )}
                          {isCurrent && <PlanBadge>Current</PlanBadge>}
                          <BillingPlanName>{plan.name}</BillingPlanName>
                          <BillingPlanDesc>{plan.description}</BillingPlanDesc>
                          <BillingPlanPrice>
                            {plan.price === 0 ? 'Free' : `$${plan.price}`}
                            {plan.price > 0 && (
                              <span>/{plan.billing_period}</span>
                            )}
                          </BillingPlanPrice>
                          <BillingFeatureList>
                            {plan.features.map((f, i) => (
                              <li key={i}>{f}</li>
                            ))}
                          </BillingFeatureList>
                          <PlanChooseBtn
                            $current={isCurrent}
                            disabled={isCurrent || subscribing === plan.id}
                            onClick={() =>
                              !isCurrent && handleSubscribe(plan.id)
                            }
                          >
                            {subscribing === plan.id
                              ? 'Subscribing…'
                              : isCurrent
                                ? 'Current Plan'
                                : 'Choose Plan'}
                          </PlanChooseBtn>
                        </PlanCard>
                      );
                    })}
                  </PlansGrid>
                </BillingSection>

                {/* Billing history */}
                <BillingSection>
                  <BillingSectionTitle>Billing History</BillingSectionTitle>
                  {invoices.length === 0 ? (
                    <div
                      style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: 'var(--gray-400)',
                        background: 'var(--gray-50)',
                        borderRadius: '0.5rem',
                      }}
                    >
                      No invoices yet.
                    </div>
                  ) : (
                    <InvoiceTable>
                      <thead>
                        <tr>
                          <th>Invoice #</th>
                          <th>Date</th>
                          <th>Plan</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => (
                          <tr key={invoice.id}>
                            <td
                              style={{
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                fontSize: '0.8125rem',
                              }}
                            >
                              {invoice.invoice_number}
                            </td>
                            <td>
                              {new Date(
                                invoice.created_at
                              ).toLocaleDateString()}
                            </td>
                            <td>{invoice.plan?.name ?? '—'}</td>
                            <td>
                              ${invoice.amount.toFixed(2)} {invoice.currency}
                            </td>
                            <td>
                              <StatusBadge $status={invoice.status}>
                                {invoice.status}
                              </StatusBadge>
                            </td>
                            <td>
                              {invoice.status !== 'paid' && (
                                <InvPayBtn
                                  disabled={payingInvoice === invoice.id}
                                  onClick={() => handlePayInvoice(invoice.id)}
                                >
                                  {payingInvoice === invoice.id ? '…' : 'Buy'}
                                </InvPayBtn>
                              )}
                              <InvPrintBtn
                                onClick={() => printInvoice(invoice)}
                              >
                                Print PDF
                              </InvPrintBtn>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </InvoiceTable>
                  )}
                </BillingSection>
              </>
            )}
          </>
        );
      }

      case 'integrations':
        return (
          <>
            <SectionTitle>Integrations</SectionTitle>
            <SectionDescription>
              Connect third-party marketplaces, payment providers, and shipping
              carriers. Each integration is an independent module — add more by
              registering new providers.
            </SectionDescription>

            {integrationsLoading && (
              <div
                style={{
                  padding: '3rem',
                  textAlign: 'center',
                  color: 'var(--gray-500)',
                }}
              >
                Loading integrations…
              </div>
            )}

            {integrationsError && (
              <div
                style={{
                  padding: '1rem',
                  color: 'var(--error-700)',
                  background: 'var(--error-50)',
                  borderRadius: '0.5rem',
                }}
              >
                {integrationsError}
              </div>
            )}

            {!integrationsLoading &&
              !integrationsError &&
              integrationGroups.map((group) => (
                <IntegrationSection key={group.category}>
                  <IntegrationSectionTitle>
                    {group.label}
                  </IntegrationSectionTitle>
                  <IntegrationsGrid>
                    {group.integrations.map((mod: IntegrationModule) => (
                      <IntegrationCard key={mod.slug}>
                        <IntegrationHeader>
                          <IntegrationLogo $color={mod.color}>
                            <IntegrationIconGlyph slug={mod.slug} />
                          </IntegrationLogo>
                          <IntegrationInfo>
                            <IntegrationName>{mod.name}</IntegrationName>
                            <IntegrationStatus $connected={mod.is_connected}>
                              {mod.is_connected ? 'Connected' : 'Not Connected'}
                            </IntegrationStatus>
                          </IntegrationInfo>
                        </IntegrationHeader>
                        <IntegrationDescription>
                          {mod.description}
                        </IntegrationDescription>
                        <IntegrationActions>
                          {mod.is_connected ? (
                            <>
                              <IntegrationButton
                                $variant="secondary"
                                onClick={() => openConfigure(mod)}
                              >
                                Configure
                              </IntegrationButton>
                              <IntegrationButton
                                $variant="danger"
                                onClick={() => handleDisconnect(mod.slug)}
                              >
                                Disconnect
                              </IntegrationButton>
                            </>
                          ) : (
                            <IntegrationButton
                              onClick={() => handleConnect(mod.slug)}
                            >
                              Connect
                            </IntegrationButton>
                          )}
                        </IntegrationActions>
                      </IntegrationCard>
                    ))}
                  </IntegrationsGrid>
                </IntegrationSection>
              ))}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <SettingsContainer>
      <SettingsContent>
        <Header>
          <Title>Settings</Title>
        </Header>

        <Main>
          <Sidebar>
            {tabs.map((tab) => (
              <NavItem
                key={tab.id}
                $active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon />
                {tab.label}
              </NavItem>
            ))}
          </Sidebar>

          <ContentPanel>{renderContent()}</ContentPanel>
        </Main>
      </SettingsContent>

      {configuringIntegration && (
        <ModalOverlay onClick={closeConfigure}>
          <ModalPanel onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Configure — {configuringIntegration.name}</ModalTitle>
              <ModalCloseButton onClick={closeConfigure}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <ModalFormRow>
                <ModalLabel>Display Name</ModalLabel>
                <ModalInput
                  value={configForm.name}
                  onChange={(e) =>
                    setConfigForm((f) => ({ ...f, name: e.target.value }))
                  }
                />
              </ModalFormRow>
              <ModalFormRow>
                <ModalLabel>API Key</ModalLabel>
                <ModalInput
                  type="password"
                  value={configForm.apiKey}
                  onChange={(e) =>
                    setConfigForm((f) => ({ ...f, apiKey: e.target.value }))
                  }
                  placeholder="Enter API key"
                />
              </ModalFormRow>
              <ModalFormRow>
                <ModalLabel>API Secret</ModalLabel>
                <ModalInput
                  type="password"
                  value={configForm.apiSecret}
                  onChange={(e) =>
                    setConfigForm((f) => ({ ...f, apiSecret: e.target.value }))
                  }
                  placeholder="Enter API secret"
                />
              </ModalFormRow>
              {configuringIntegration.slug === 'amazon' && (
                <ModalFormRow>
                  <ModalLabel>Region</ModalLabel>
                  <ModalSelect
                    value={configForm.region}
                    onChange={(e) =>
                      setConfigForm((f) => ({ ...f, region: e.target.value }))
                    }
                  >
                    <option value="us-east-1">US East (N. Virginia)</option>
                    <option value="us-west-2">US West (Oregon)</option>
                    <option value="eu-west-1">EU (Ireland)</option>
                    <option value="eu-central-1">EU (Frankfurt)</option>
                    <option value="ap-southeast-1">
                      Asia Pacific (Singapore)
                    </option>
                  </ModalSelect>
                </ModalFormRow>
              )}
              <ModalFormRow>
                <ModalLabel>Sync Interval</ModalLabel>
                <ModalSelect
                  value={configForm.syncInterval}
                  onChange={(e) =>
                    setConfigForm((f) => ({
                      ...f,
                      syncInterval: e.target.value,
                    }))
                  }
                >
                  <option value="5">Every 5 minutes</option>
                  <option value="15">Every 15 minutes</option>
                  <option value="30">Every 30 minutes</option>
                  <option value="60">Every hour</option>
                  <option value="360">Every 6 hours</option>
                  <option value="1440">Once a day</option>
                </ModalSelect>
              </ModalFormRow>
              <ModalFormRow>
                <ModalToggleRow>
                  <div>
                    <ModalToggleLabel>Auto Sync</ModalToggleLabel>
                    <ModalToggleDesc>
                      Automatically sync products on the selected interval
                    </ModalToggleDesc>
                  </div>
                  <ModalToggle
                    type="checkbox"
                    checked={configForm.autoSync}
                    onChange={(e) =>
                      setConfigForm((f) => ({
                        ...f,
                        autoSync: e.target.checked,
                      }))
                    }
                  />
                </ModalToggleRow>
              </ModalFormRow>
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={closeConfigure}>Cancel</ModalCancelBtn>
              <ModalSaveBtn onClick={closeConfigure}>Save Changes</ModalSaveBtn>
            </ModalFooter>
          </ModalPanel>
        </ModalOverlay>
      )}
    </SettingsContainer>
  );
};

export default Settings;
